import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth } from "./auth/AuthProvider";
import { trackEvent } from "./api/analytics";
import { activeConversation } from "./auth/activeConversation";
import { useI18n, LangToggle } from "./i18n";
import { LoginScreen } from "./screens/LoginScreen";
import { RegisterScreen } from "./screens/RegisterScreen";
import { AgentScreen } from "./screens/AgentScreen";
import { LocalAgentScreen } from "./screens/LocalAgentScreen";
import { AdminScreen } from "./screens/AdminScreen";
import { Sidebar } from "./screens/Sidebar";
import { ConversationList } from "./screens/ConversationList";
import { SettingsModal } from "./screens/SettingsModal";
import { BillingModal } from "./screens/BillingModal";
import { CronModal } from "./screens/CronModal";
import { PurchaseScreen } from "./screens/PurchaseScreen";
import { ResearcherScreen } from "./screens/ResearcherScreen";
import { WorkspaceScreen } from "./screens/workspace/WorkspaceScreen";
import { AppRail, type WorkspaceView } from "./screens/workspace/AppRail";
import { ChatSidebar } from "./screens/workspace/ChatSidebar";
import { SettingsScreen } from "./screens/workspace/SettingsScreen";
import { resetMobileSurfaceScroll, resolveInitialSurface, type AppSurface } from "./lib/workspaceNavigation";

function shouldOpenBillingFromUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  const billing = params.get("billing")?.toLowerCase();
  return billing === "1" || billing === "true" || billing === "yes";
}

function isPurchasePage(): boolean {
  const params = new URLSearchParams(window.location.search);
  const purchase = params.get("purchase")?.toLowerCase();
  return window.location.pathname === "/purchase" || purchase === "1" || purchase === "true" || purchase === "yes";
}

function clearBillingFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("billing");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function SignedInApp() {
  const { account, accounts, user, isAdmin, switchAccount, signOut } = useAuth();
  const { t } = useI18n();
  const isLocal = account?.agentMode === "local";

  // null = a fresh "new chat"; a string = the conversation being viewed/continued.
  // AgentScreen reacts to changes in place (no remount: replacing the composer
  // DOM mid-interaction could swallow a message typed right after the switch).
  // Restore the conversation the user was last in if they're back within the
  // resume window (backgrounding / reload); otherwise start a fresh chat.
  const [conversationId, setConversationId] = useState<string | null>(() => activeConversation.load());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [cronOpen, setCronOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  // Local mode: the device-tasks drawer (LocalHistoryDrawer), opened from the sidebar.
  const [tasksOpen, setTasksOpen] = useState(false);
  const [sessionsVersion, setSessionsVersion] = useState(0);
  // Local mode: bumping this tells LocalAgentScreen to start a fresh chat.
  const [localReset, setLocalReset] = useState(0);
  const [surface, setSurface] = useState<AppSurface>(() =>
    resolveInitialSurface({
      search: window.location.search,
      activeAccountId: account?.id ?? null,
      primaryAccountId: accounts[0]?.id ?? null
    })
  );
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>("chat");
  // Mobile only: whether the chat surface shows the conversation (detail) vs. the
  // history list. Ignored on desktop, where both panes are always visible.
  const [chatDetail, setChatDetail] = useState(false);
  const appBodyRef = useRef<HTMLDivElement | null>(null);
  const refreshSessions = useCallback(() => setSessionsVersion((v) => v + 1), []);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const openConversation = useCallback((id: string) => {
    setAdminOpen(false);
    setConversationId(id);
    setChatDetail(true); // mobile: slide into the conversation
  }, []);
  const newChat = useCallback(() => {
    setAdminOpen(false);
    setSurface("chat");
    setConversationId(null);
    setChatDetail(true); // mobile: a fresh conversation is the detail pane
    setLocalReset((v) => v + 1);
  }, []);

  // Keep the persisted "active conversation" pointer in sync so a reload or
  // return-from-background reopens the right chat (see auth/activeConversation).
  useEffect(() => {
    if (conversationId) activeConversation.save(conversationId);
    else activeConversation.clear();
  }, [conversationId]);

  useLayoutEffect(() => {
    resetMobileSurfaceScroll(
      typeof window.matchMedia === "function" && window.matchMedia("(max-width: 720px)").matches,
      appBodyRef.current,
      window
    );
  }, [surface, account?.id]);

  useEffect(() => {
    if (isLocal || !shouldOpenBillingFromUrl()) return;
    setBillingOpen(true);
    clearBillingFromUrl();
  }, [isLocal]);

  // Re-stamp the pointer when the user leaves so the resume window counts from
  // when they backgrounded the app, not from their last message.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") activeConversation.touch();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onHidden);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onHidden);
    };
  }, []);

  // Everything except admin / legacy-local rides a shared left icon rail (no top
  // header). The rail carries the workspace switcher + Settings.
  const showRail = !adminOpen && !isLocal;

  return (
    <div className={`app-shell ${adminOpen ? "admin-mode" : ""} ${showRail ? "has-rail" : ""} ${surface === "research" && showRail ? "research-mode" : ""}`}>
      {adminOpen ? (
        <AdminScreen />
      ) : isLocal ? (
        <LocalAgentScreen
          drawerOpen={tasksOpen}
          settingsOpen={settingsOpen}
          onCloseDrawer={() => setTasksOpen(false)}
          onCloseSettings={() => setSettingsOpen(false)}
          resetSignal={localReset}
        />
      ) : (
        <div className="app-body" ref={appBodyRef}>
          <AppRail
            surface={surface}
            onPickChat={() => { const p = accounts[0]; if (p) switchAccount(p.id); setSurface("chat"); setChatDetail(false); }}
            onPickResearch={() => { const p = accounts[0]; if (p) switchAccount(p.id); setSurface("research"); }}
            onPickWorkspace={(id) => { switchAccount(id); setSurface("workspace"); }}
            onCreated={() => setSurface("workspace")}
            workspaceView={workspaceView}
            onWorkspaceView={setWorkspaceView}
            onOpenSettings={() => setSurface("settings")}
          />
          {surface === "settings" ? (
            <SettingsScreen />
          ) : surface === "research" ? (
            <ResearcherScreen />
          ) : surface === "workspace" ? (
            <WorkspaceScreen view={workspaceView} />
          ) : (
            <div className={`ws-view${chatDetail ? " detail" : ""}`}>
              <ChatSidebar
                conversationId={conversationId}
                version={sessionsVersion}
                onSelect={openConversation}
                onNewChat={newChat}
              />
              <section className="ws-content ws-content-chat">
                <button type="button" className="ws-mback" onClick={() => setChatDetail(false)}>‹ {t("workspace.chat")}</button>
                <AgentScreen
                  key={account?.id ?? "no-account"}
                  initialConversationId={conversationId}
                  onConversationStarted={setConversationId}
                  onSessionsChanged={refreshSessions}
                  onResumeExpired={newChat}
                />
              </section>
            </div>
          )}
        </div>
      )}

      <Sidebar
        open={sidebarOpen}
        isLocal={isLocal}
        email={user?.email}
        onClose={closeSidebar}
        onNewChat={newChat}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenCron={isLocal ? undefined : () => setCronOpen(true)}
        onOpenBilling={isLocal ? undefined : () => setBillingOpen(true)}
        onOpenAdmin={isAdmin ? () => setAdminOpen(true) : undefined}
        onSignOut={() => void signOut()}
      >
        {isLocal ? (
          <button type="button" className="history-item" onClick={() => { setTasksOpen(true); closeSidebar(); }}>
            <span className="h-title">{t("nav.tasks")}</span>
          </button>
        ) : (
          <ConversationList
            selectedConversationId={conversationId}
            version={sessionsVersion}
            onSelect={(id) => {
              openConversation(id);
              closeSidebar();
            }}
          />
        )}
      </Sidebar>

      {!isLocal ? <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} /> : null}
      {!isLocal ? <BillingModal open={billingOpen} onClose={() => setBillingOpen(false)} /> : null}
      {!isLocal ? <CronModal open={cronOpen} onClose={() => setCronOpen(false)} /> : null}
    </div>
  );
}

export function App() {
  const { ready, user, baseUrl } = useAuth();
  const [view, setView] = useState<"login" | "register">("login");

  // Funnel: one `visit` per app load (top of the acquisition funnel).
  useEffect(() => {
    trackEvent(baseUrl, "visit", { referrer: document.referrer || null, lang: navigator.language });
    // baseUrl is fixed by build mode; emit once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) {
    return (
      <div className="center">
        <div className="spinner" />
      </div>
    );
  }
  if (isPurchasePage()) return <PurchaseScreen />;
  if (user) return <SignedInApp />;
  return (
    <>
      <LangToggle className="floating" />
      {view === "login" ? (
        <LoginScreen onRegister={() => setView("register")} />
      ) : (
        <RegisterScreen onLogin={() => setView("login")} />
      )}
    </>
  );
}
